import { useEffect, useState, useRef } from 'react';
import { X, GearSix } from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { usePlaybackStore } from '../../stores/playbackStore';

const CANVAS_PRESETS = [
  { label: '1920 × 1080', w: 1920, h: 1080 },
  { label: '1280 × 720',  w: 1280, h: 720  },
  { label: '2560 × 1440', w: 2560, h: 1440 },
  { label: '3840 × 2160', w: 3840, h: 2160 },
];

interface ScenePropertiesDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function ScenePropertiesDialog({ open, onClose }: ScenePropertiesDialogProps) {
  const { sceneName, setSceneName, canvasWidth, canvasHeight, setCanvasSize, backgroundColor, setBackgroundColor, save } = useSceneStore();
  const { autoPlayOnLoad, setAutoPlayOnLoad } = usePlaybackStore();

  const [name, setName] = useState(sceneName);
  const [w, setW] = useState(String(canvasWidth));
  const [h, setH] = useState(String(canvasHeight));
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName(sceneName);
      setW(String(canvasWidth));
      setH(String(canvasHeight));
      setTimeout(() => nameRef.current?.select(), 50);
    }
  }, [open, sceneName, canvasWidth, canvasHeight]);

  if (!open) return null;

  function apply() {
    const trimmed = name.trim();
    if (trimmed) setSceneName(trimmed);
    const pw = parseInt(w, 10);
    const ph = parseInt(h, 10);
    if (pw > 0 && ph > 0) setCanvasSize(pw, ph);
    save().catch(console.error);
    onClose();
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') apply();
    if (e.key === 'Escape') onClose();
    e.stopPropagation();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-bg-elevated border border-border rounded-xl shadow-2xl w-[360px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border-subtle">
          <GearSix size={14} className="text-accent" />
          <span className="font-serif text-base font-medium text-accent tracking-[0.01em] flex-1">Scene Properties</span>
          <button onClick={onClose} className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast">
            <X size={13} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest mb-1.5">
              Scene Name
            </label>
            <input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Scene name…"
              className="w-full bg-bg-input border border-border text-text-primary rounded-md px-3 py-[7px] text-sm font-sans
                         hover:bg-bg-hover focus:outline-none focus:border-accent transition-all duration-normal"
            />
          </div>

          {/* Canvas Size */}
          <div>
            <label className="block font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest mb-1.5">
              Canvas Size
            </label>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1.5 flex-1">
                <span className="font-mono text-xs text-error font-medium w-4 shrink-0">W</span>
                <input
                  type="number"
                  value={w}
                  onChange={(e) => setW(e.target.value)}
                  min={1}
                  className="w-full bg-bg-input border border-border text-text-primary rounded-md px-2.5 py-[5px] text-xs font-mono
                             hover:bg-bg-hover focus:outline-none focus:border-accent transition-all duration-normal"
                />
              </div>
              <span className="text-text-tertiary text-xs font-mono">×</span>
              <div className="flex items-center gap-1.5 flex-1">
                <span className="font-mono text-xs text-sage font-medium w-4 shrink-0">H</span>
                <input
                  type="number"
                  value={h}
                  onChange={(e) => setH(e.target.value)}
                  min={1}
                  className="w-full bg-bg-input border border-border text-text-primary rounded-md px-2.5 py-[5px] text-xs font-mono
                             hover:bg-bg-hover focus:outline-none focus:border-accent transition-all duration-normal"
                />
              </div>
            </div>
            {/* Presets */}
            <div className="grid grid-cols-2 gap-1">
              {CANVAS_PRESETS.map((p) => (
                <button
                  key={`${p.w}x${p.h}`}
                  onClick={() => { setW(String(p.w)); setH(String(p.h)); }}
                  className={`text-left px-2.5 py-1.5 rounded-md text-[11px] font-mono transition-all duration-fast ${
                    String(p.w) === w && String(p.h) === h
                      ? 'bg-accent-muted text-accent border border-accent/30'
                      : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-transparent'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Background Color */}
          <div>
            <label className="block font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest mb-1.5">
              Background
            </label>
            <div className="flex items-center gap-2">
              <button
                title="Transparent"
                onClick={() => setBackgroundColor(null)}
                className={`w-7 h-7 rounded-md border flex-shrink-0 transition-all duration-fast ${
                  backgroundColor === null ? 'border-accent ring-1 ring-accent/40' : 'border-border hover:border-border-hover'
                }`}
                style={{ background: 'repeating-conic-gradient(#555 0% 25%, #333 0% 50%) 0 0 / 6px 6px' }}
              />
              <label className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border hover:border-border-hover bg-bg-input hover:bg-bg-hover cursor-pointer transition-all duration-fast flex-1">
                <span
                  className="w-4 h-4 rounded border border-border shrink-0"
                  style={{ backgroundColor: backgroundColor ?? '#000000' }}
                />
                <span className="font-mono text-xs text-text-secondary flex-1">
                  {backgroundColor ? backgroundColor.toUpperCase() : 'Transparent'}
                </span>
                <input
                  type="color"
                  value={backgroundColor ?? '#000000'}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  className="sr-only"
                />
              </label>
            </div>
          </div>

          {/* Playback */}
          <div>
            <label className="block font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest mb-1.5">
              Playback
            </label>
            <button
              role="switch"
              aria-checked={autoPlayOnLoad}
              onClick={() => setAutoPlayOnLoad(!autoPlayOnLoad)}
              className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md border border-border hover:border-border-hover hover:bg-bg-hover transition-all duration-fast"
            >
              <span
                className={`relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors duration-normal ${
                  autoPlayOnLoad ? 'bg-accent' : 'bg-bg-input border border-border'
                }`}
              >
                <span
                  className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform duration-normal ${
                    autoPlayOnLoad ? 'translate-x-3' : 'translate-x-0.5'
                  }`}
                />
              </span>
              <span className="font-mono text-xs text-text-secondary">Auto play on load</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-4 py-3 border-t border-border-subtle">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-[7px] rounded-md text-sm font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-border transition-all duration-fast"
          >
            Cancel
          </button>
          <button
            onClick={apply}
            className="flex-1 px-3 py-[7px] rounded-md text-sm font-sans font-medium bg-accent text-white hover:bg-accent-hover hover:shadow-glow transition-all duration-normal"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
